import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AddBlockedPeriodPolicyDocument,
  AddBlockedPeriodPolicyMutation,
  FetchTimeoffDataDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import NotFound from 'modules/core/components/NotFound'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import PolicyForm from 'modules/timeoff/lib/BlockedPeriods/PolicyCreateForm'

interface Props {
  onClose: () => void
  onSuccess: (id: string) => void
  defaultLeaveTypeId: string
  canManageTimeOff: boolean
}

const AddBlockedPeriodModal = ({
  onClose,
  onSuccess,
  defaultLeaveTypeId,
  canManageTimeOff,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const form = useMemo(() => new PolicyForm({ name: '' }), [])

  const { company } = useCurrentSession()

  const [addBlockedPeriodPolicyMutation] = useMutation(AddBlockedPeriodPolicyDocument, {
    extractorFn: (mutationData: AddBlockedPeriodPolicyMutation) =>
      mutationData.timeoffMutations.createTimeoffBlockedPeriod,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await addBlockedPeriodPolicyMutation({
      variables: {
        name: form.get('name').value,
        companyId: company.id,
        leaveTypeIds: [defaultLeaveTypeId],
      },
      refetchQueries: [FetchTimeoffDataDocument],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      closePortal()
      if (handled.data.blockedPeriod) {
        onSuccess(handled.data.blockedPeriod.id)
        navigate(
          navigation.settings.timeoffPolicies.blockedPeriodsId.fullPath({
            id: handled.data.blockedPeriod.id,
          })
        )
      }
    }
  }

  if (!canManageTimeOff) return <NotFound />

  return (
    <Modal
      illustration="holidays-event-go"
      title={i18n.t('settings.timeoff_blocked_periods.add.title')}
      description={i18n.t('settings.timeoff_blocked_periods.add.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button disabled={!form.isValid} submit>
          {i18n.t('settings.timeoff_blocked_periods.add.button')}
        </Button>
      }
    >
      <Text
        label={i18n.t('settings.timeoff_blocked_periods.fields.name')}
        field={form.get('name')}
      />
    </Modal>
  )
}

export default observer(AddBlockedPeriodModal)
